import axios from 'axios';
import { useNavigate } from 'react-router';
import { SnackbarMessage, OptionsObject } from 'notistack';

const ACCOUNTS_CUSTOMERS_URL = '/accounts/customers';
const COUNT_URL = '/count';
const LOGIN_URL = '/accounts/login';
const RESET_PASSWORD = '/reset-password';
const ACCOUNTS_USERS_URL_START = '/accounts';
const ACCOUNTS_USERS_URL_END = '/users';
const SITES_ROOT = '/sites';
const GATEWAYS_URL_END = '/gateways';
const ACCOUNTS_USERS_FORGOT_PASSWORD = '/forgot-password';
const ACCOUNTS_USERS_REPLACE_PASSWORD = '/replace-password';
const ACCOUNTS_USERS_SET_PASSWORD = '/set-password';
const ACCOUNTS_JOB_TITLES = '/job-titles';
const WIDGETS = 'widgets';
const ASSETS_MANAGEMENT_URL = '/assets';
const ASSETS_PROPERTIES = '/properties';
const ASSETS_PROPERTY = '/property';
const ASSETS_TAGS = '/tags';
const ASSETS_ALARM_TAGS = '/alarm-tags';
const VALUES = '/values';
const ALARMS_URL = '/alarms';
const EVENTS = '/events';
const CUSTOMER_EVENTS = '/customer-events';
const READINGS = '/readings';
const PUMP_REPORT = '/pump-report';
const REFRESH = '/refresh';
const NOTIFICATIONS_URL = '/notifications';
const UNSUBSCRIBE = '/unsubscribe';
const DISCONNECT = 'disconnect';
const SET_PROFILE_URL = 'accounts/set-profile';
const CONTROLLERS_URL = '/controllers';
const CONTROLLER_URL = '/controller';
const NOTIFICATION_GROUPS_URL = '/notification-groups';
const CHECK_USERS_SITES_ACCESS = 'check-users-sites-access';
const SITE_PREVIEWS = 'site/previews';
const THEMES = 'themes';
const API_V2 = '/v2';
const API_V3 = '/v3';
const REPORTS = 'reports';
const CUSTOMERS = 'customers';
const TEMPLATES = 'templates';
const ALARM_RULES = 'alarm-rules';
const OTP_CHALLENGE_URL = '/accounts/login/otp-challenge';
const LOGIN_TOTP_CHALLENGE_URL = '/accounts/login/totp-challenge';
const LOGIN_SMS_CHALLENGE_URL = '/accounts/login/sms-challenge';
const SETUP_TOTP_MFA_URL = '/accounts/setup-totp-mfa';
const VERIFY_SOFTWARE_TOKEN_URL = '/accounts/verify-software-token';
const SETUP_SMS_MFA_URL = '/accounts/setup-sms-mfa';
const VERIFY_PHONE_NUMBER_URL = '/accounts/verify-phone-number';
const SECURITY_POLICY_SUFFIX = '/security-policy';
const RESET_USER_MFA_SUFFIX = '/reset-user-mfa';

type EnqueueSnackbar = (message: SnackbarMessage, options?: OptionsObject) => void;

const api = axios.create({
  baseURL: import.meta.env.VITE_STREAMETRIC_API,
  withCredentials: true,
});

const ERROR_MESSAGES = [
  'unable to refresh access token',
  "('cookie', 'access_token')",
  're-authentication',
];

// Exact 401 `detail` message that means the access token was revoked server-side.
const REVOKED_ACCESS_TOKEN_MESSAGE = 'access token has been revoked';

// Guards against triggering the logout flow multiple times when several
// requests fail with a revoked/expired token at the same time.
let isLoggingOut = false;

const setupInterceptors = (
  navigate: ReturnType<typeof useNavigate>,
  logout: () => void,
  enqueueSnackbar: EnqueueSnackbar
) =>
  api.interceptors.response.use(
    (response) => response,
    (error) => {
      const errorMessage = error?.response?.data;
      const errorStatus = error?.response?.status === 400 || error?.response?.status === 401;

      // take a look at this
      // eslint-disable-next-line array-callback-return
      const includedErrorMessage = ERROR_MESSAGES.some((k) => {
        if (typeof errorMessage?.detail === 'string')
          return errorMessage?.detail?.toLowerCase().includes(k);
        if (typeof errorMessage?.detail?.message === 'string')
          return errorMessage?.detail?.message?.toLowerCase().includes(k);
      });

      // Specifically a 401 whose detail is exactly "Access Token has been revoked".
      const isRevokedAccessToken =
        error?.response?.status === 401 &&
        typeof errorMessage?.detail === 'string' &&
        errorMessage.detail.toLowerCase() === REVOKED_ACCESS_TOKEN_MESSAGE;

      if ((errorStatus && includedErrorMessage) || isRevokedAccessToken) {
        if (!isLoggingOut) {
          isLoggingOut = true;
          // logout() is async; reset the guard once it settles so a future
          // session can log out again if its token is later revoked.
          Promise.resolve(logout()).finally(() => {
            isLoggingOut = false;
          });
          navigate('/login');
          enqueueSnackbar('Your session expired. Please sign in again.', { variant: 'error' });
        }
      }
      return Promise.reject(error);
    }
  );

export {
  setupInterceptors,
  ACCOUNTS_CUSTOMERS_URL,
  COUNT_URL,
  LOGIN_URL,
  RESET_PASSWORD,
  api,
  ACCOUNTS_USERS_URL_START,
  ACCOUNTS_USERS_URL_END,
  GATEWAYS_URL_END,
  ACCOUNTS_JOB_TITLES,
  ACCOUNTS_USERS_FORGOT_PASSWORD,
  ACCOUNTS_USERS_SET_PASSWORD,
  ACCOUNTS_USERS_REPLACE_PASSWORD,
  WIDGETS,
  ASSETS_MANAGEMENT_URL,
  ASSETS_PROPERTIES,
  ASSETS_PROPERTY,
  ASSETS_TAGS,
  ASSETS_ALARM_TAGS,
  VALUES,
  ALARMS_URL,
  EVENTS,
  READINGS,
  PUMP_REPORT,
  REFRESH,
  NOTIFICATIONS_URL,
  UNSUBSCRIBE,
  DISCONNECT,
  SET_PROFILE_URL,
  SITES_ROOT,
  CONTROLLERS_URL,
  CONTROLLER_URL,
  CUSTOMER_EVENTS,
  NOTIFICATION_GROUPS_URL,
  CHECK_USERS_SITES_ACCESS,
  SITE_PREVIEWS,
  THEMES,
  API_V2,
  API_V3,
  REPORTS,
  CUSTOMERS,
  TEMPLATES,
  ALARM_RULES,
  OTP_CHALLENGE_URL,
  LOGIN_TOTP_CHALLENGE_URL,
  LOGIN_SMS_CHALLENGE_URL,
  SETUP_TOTP_MFA_URL,
  VERIFY_SOFTWARE_TOKEN_URL,
  SETUP_SMS_MFA_URL,
  VERIFY_PHONE_NUMBER_URL,
  SECURITY_POLICY_SUFFIX,
  RESET_USER_MFA_SUFFIX,
};
